import {
  HStack,
  Box,
  VStack,
  Grid,
  GridItem,
  Text,
  Center,
} from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { BlockContainer } from "src/atoms";
import { GatsbyImage } from "gatsby-plugin-image";
import Renderer from "src/lib/renderer";
import {
  heading1,
  heading2,
  heading3,
  heading5,
} from "src/lib/renderer/elements/headings";
import paragraph from "src/lib/renderer/elements/paragraph";
import { Element } from "@prismicio/react";
import { SvgOrImg } from "src/components/SvgOrImg";
import {
  areOrderedBreakpoints,
  isValidDesktopMediaBreakpoint,
} from "../BlockEmbedHubspotForm/utils";
import { clamp } from "../utils";
import { marketoStyles } from "./marketoStyles";
import { MarketoSkeleton } from "./MarketoSkeleton";

declare global {
  interface Window {
    MktoForms2?: {
      loadForm: (
        baseUrl: string,
        munchkinId: string,
        formId: string,
        callback?: (form: any) => void,
      ) => void;
    };
    dataLayer?: any[];
  }
}

const BlockEmbedMarketoForm: FC<
  (
    | Gatsby.PageBlockEmbedMarketoFormFragment
    | Gatsby.PageLandingBlockEmbedMarketoFormFragment
    | Gatsby.PageFragmentBlockEmbedMarketoFormFragment
  ) & { index: number; items?: any }
> = ({ primary, items }) => {
  const [formLoaded, setFormLoaded] = useState(false);

  if (!primary) return null;

  const {
    content,
    form_id,
    munchkin_id,
    base_url,
    follow_up_url,
    layout_type,
    cta_subtitle,
    cta_subicon,
    media_1: media1,
    media_2: media2,
    media_3: media3,
    media_2_breakpoint: media2BreakpointUnsafe,
    media_3_breakpoint: media3BreakpointUnsafe,
    form_min_width: formMinWidthUnsafe,
    form_min_height: formMinHeightUnsafe,
  } = primary;

  if (!form_id || !munchkin_id || !base_url) {
    return null;
  }

  const isGraphicsLayout = layout_type === "With Graphics";
  const hasValidItems = items && items.length > 0 && !!items[0]?.label;

  const media2Breakpoint = isValidDesktopMediaBreakpoint(media2BreakpointUnsafe)
    ? media2BreakpointUnsafe
    : "lg";
  const media3BreakpointUnordered = isValidDesktopMediaBreakpoint(
    media3BreakpointUnsafe,
  )
    ? media3BreakpointUnsafe
    : "xl";
  const media3Breakpoint = areOrderedBreakpoints(
    media2Breakpoint,
    media3BreakpointUnordered,
  )
    ? media3BreakpointUnordered
    : media2Breakpoint;

  const formMinWidth = clamp(300, 700, 500, formMinWidthUnsafe);
  const formMinHeight = clamp(
    100,
    Number.MAX_SAFE_INTEGER,
    800,
    formMinHeightUnsafe,
  );

  useEffect(() => {
    if (formLoaded) return;

    const scriptSrc = `${base_url}/js/forms2/js/forms2.min.js`;

    const enhanceDynamicFields = (formElem: HTMLElement) => {
      formElem
        .querySelectorAll('.mktoField, input[type="checkbox"]')
        .forEach((field) => {
          const row = field.closest(".mktoFormRow");
          if (row) {
            if (
              field.tagName.toLowerCase() === "textarea" &&
              !row.classList.contains("row-textarea")
            ) {
              row.classList.add("row-textarea");
            }
            if (
              field.getAttribute("type") === "checkbox" &&
              !row.classList.contains("row-checkbox")
            ) {
              row.classList.add("row-checkbox");
            }
          }
        });
    };

    const initForm = () => {
      if (window.MktoForms2) {
        window.MktoForms2.loadForm(base_url, munchkin_id, form_id, (form) => {
          form.onSuccess((values: any, followUpUrl: string) => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "marketo-form-submit",
              "form-id": form.getId(),
              "form-type": values?.formtype,
            });            
            // Prioritize Prismic follow_up_url, then Marketo dashboard followUpUrl, 
            // then the temporary hardcoded guide URL.
            const isMarketoSelfRedirect = followUpUrl && followUpUrl.includes("?aliId=");
            const finalRedirectUrl = follow_up_url || (isMarketoSelfRedirect ? null : followUpUrl) || "https://www.shine.fr/experts-comptables/guides/";
            
            if (finalRedirectUrl) {
              setTimeout(() => {
                window.location.href = finalRedirectUrl;
              }, 5000);
            }
            
            return false;
          });



          const formEl = form.getFormElem()[0];
          if (formEl) enhanceDynamicFields(formEl);
          setFormLoaded(true);
        });
      }
    };

    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

    if (existingScript) {
      if (window.MktoForms2) {
        initForm();
      } else {
        existingScript.addEventListener("load", initForm);
      }
    } else {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;
      script.addEventListener("load", initForm);
      document.body.appendChild(script);
    }
  }, [base_url, munchkin_id, form_id, formLoaded, follow_up_url]);

  const hasContent = !!(
    content?.raw &&
    Array.isArray(content.raw) &&
    content.raw.some((block: any) => block.text && block.text.trim().length > 0)
  );

  const renderItems = (renderableItems: any[]) => {
    return hasValidItems ? (
      <VStack align="flex-start" spacing="space-4" w="full">
        {renderableItems?.map((item) => {
          if (!item) return null;
          return (
            <HStack key={item.label} spacing="space-8" align="flex-start">
              <Center
                w={{ base: "28px", md: "32px" }}
                h={{ base: "28px", md: "32px" }}
                flexShrink="0"
                borderRadius="md"
                justifyContent="center"
                alignItems="center"
              >
                <SvgOrImg
                  imageFile={item.icon?.document?.data?.image_file}
                  isBox={true}
                  size={16}
                  colorChanges={[["#25241D", "white"]]}
                />
              </Center>
              <Box>
                <Text
                  textAlign="left"
                  variant="body-sm-reg"
                  mt={{ base: "3px", md: "8px" }}
                  textColor="white"
                  fontFamily="body"
                >
                  {item.label}
                </Text>
              </Box>
            </HStack>
          );
        })}
      </VStack>
    ) : null;
  };

  const renderImg = (image: any) => {
    const imageFile = image?.document?.data?.image_file;
    if (!imageFile) return null;

    return (
      <Box
        as={GatsbyImage}
        borderRadius="intermediate"
        maxHeight={{ base: "250px", sm: "300px", md: "unset" }}
        image={imageFile.gatsbyImageData}
        alt={imageFile.alt ?? ""}
        sx={{ aspectRatio: { base: "unset", md: "1" } }}
      />
    );
  };

  const renderSubtitleText = (icon: any, title?: string) => {
    if (!title && !icon) return null;
    return (
      <HStack
        key={title}
        spacing="space-2"
        align="flex-start"
        justifyContent="center"
        mt="space-16"
      >
        <Center
          w="24px"
          h="24px"
          flexShrink="0"
          borderRadius="md"
          justifyContent="center"
          alignItems="center"
        >
          <SvgOrImg
            imageFile={icon?.document?.data?.image_file}
            isBox={true}
            size={12}
          />
        </Center>

        {title ? (
          <Box>
            <Text
              as="div"
              textAlign="left"
              mt="2px"
              fontSize={{ base: "font-16", md: "font-15" }}
              lineHeight={{ base: 1.3, md: 1.5 }}
              fontFamily="body"
              textColor="grey-1"
            >
              {title}
            </Text>
          </Box>
        ) : null}
      </HStack>
    );
  };

  const renderBlock = () => {
    return (
      <VStack
        textAlign={{ base: "left" }}
        spacing={{ base: "space-16", md: "space-24" }}
        align={{ base: "flex-start" }}
        style={{
          display: "flex",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        <VStack spacing="space-16" align={{ base: "flex-start" }}>
          <Renderer
            overrides={{
              [Element.heading2]: (args) =>
                heading1({
                  ...args,
                  overrideProps: { marginBottom: 0, textColor: "white" },
                }),
              [Element.heading3]: (args) =>
                heading5({
                  ...args,
                  overrideProps: { marginBottom: 0, textColor: "white" },
                }),
              [Element.paragraph]: (args) =>
                paragraph({ ...args, overrideProps: { textColor: "white" } }),
            }}
            field={content}
          />
        </VStack>
        {hasValidItems ? (
          <Box display={{ base: "none", md: "block" }}>
            {renderItems(items)}
          </Box>
        ) : null}
      </VStack>
    );
  };

  const renderFormContainer = () => (
    <Box
      w="full"
      maxWidth={isGraphicsLayout ? "unset" : "960px"}
      mx="auto"
      overflow="hidden"
      boxShadow="card"
      bg={isGraphicsLayout ? "prosperity-stone-100" : "white"}
      borderRadius="intermediate"
      padding={{
        base: "space-20",
        md: isGraphicsLayout ? "space-32" : "space-24",
      }}
      minHeight={
        isGraphicsLayout ? { base: "400px", md: `${formMinHeight}px` } : "unset"
      }
      sx={marketoStyles}
    >
      {!formLoaded && <MarketoSkeleton />}

      <Box
        display={formLoaded ? "block" : "none"}
        w="full"
        opacity={formLoaded ? 1 : 0}
        transition="opacity 0.3s"
      >
        <form id={`mktoForm_${form_id}`} style={{ width: "100%" }} />
        {cta_subtitle || cta_subicon
          ? renderSubtitleText(cta_subicon, cta_subtitle)
          : null}
      </Box>
    </Box>
  );

  if (isGraphicsLayout) {
    return (
      <BlockContainer paddingY="space-40">
        <Grid
          w="full"
          gridTemplateRows={{ base: "auto", md: "1fr auto" }}
          gridTemplateColumns={{
            base: "auto",
            md: "repeat(2, 1fr)",
            ...{ [media2Breakpoint]: "repeat(3, 1fr)" },
            ...{ [media3Breakpoint]: "repeat(4, 1fr)" },
          }}
          gridGap="space-32"
          alignItems="start"
        >
          <GridItem
            colSpan={{
              base: 1,
              [media2Breakpoint]: 2,
              ...{ [media3Breakpoint]: 3 },
            }}
            alignSelf="stretch"
            bg="primary-green"
            borderRadius="intermediate"
            color="white"
            textAlign="left"
            padding="space-32"
          >
            {renderBlock()}
          </GridItem>
          <GridItem
            rowSpan={{ base: 1, md: 2 }}
            minWidth={{ base: "full", md: `${formMinWidth}px` }}
          >
            {renderFormContainer()}
          </GridItem>
          <GridItem>{renderImg(media1)}</GridItem>
          <GridItem display={{ base: "none", [media2Breakpoint]: "grid" }}>
            {renderImg(media2)}
          </GridItem>
          <GridItem display={{ base: "none", [media3Breakpoint]: "grid" }}>
            {renderImg(media3)}
          </GridItem>
          {hasValidItems ? (
            <GridItem
              alignSelf="stretch"
              bg="primary-green"
              borderRadius="intermediate"
              color="white"
              textAlign="left"
              padding="space-32"
              display={{ base: "block", md: "none" }}
            >
              {renderItems(items)}
            </GridItem>
          ) : null}
        </Grid>
      </BlockContainer>
    );
  }

  return (
    <BlockContainer paddingY={{ base: "space-40", md: "space-64" }}>
      <VStack w="full" spacing="space-32" alignItems="center">
        {hasContent ? (
          <Box w="full" textAlign="center">
            <Renderer
              overrides={{
                [Element.heading2]: (args) =>
                  heading2({ ...args, overrideProps: { textAlign: "center" } }),
                [Element.heading3]: (args) =>
                  heading3({ ...args, overrideProps: { textAlign: "center" } }),
                [Element.paragraph]: (args) =>
                  paragraph({
                    ...args,
                    overrideProps: { textColor: "grey-1", textAlign: "center" },
                  }),
              }}
              field={content}
            />
          </Box>
        ) : null}
        {renderFormContainer()}
      </VStack>
    </BlockContainer>
  );
};

export default BlockEmbedMarketoForm;
